import { createSlice } from '@reduxjs/toolkit';
import { IObjectAssociatedVersion, IObjectVersion } from 'common/interfaces/version';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { getObjectsOfNode } from '../object/object';
import { getUnitTemplateQuantitiesGroupedSelector } from '../unit-templates/selectors';

export const VERSION_PAGE_SIZE = 20;

const version = createSlice({
  name: 'version',
  initialState: {
    isLoading: false,
    isRestoring: false,
    error: null as string | null,
    versions: {} as { [objectid: string]: IObjectVersion[] },
    hasMore: {} as { [objectid: string]: boolean },
    associatedVersions: {} as { [objectid: string]: IObjectAssociatedVersion[] },
    associatedHasMore: {} as { [objectid: string]: boolean },
    versionData: {} as { [versionid: string]: unknown },
    associatedVersionData: {} as { [versionid: string]: unknown },
  },
  reducers: {
    versionsRequested: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    versionsReceived: (state, action) => {
      const { objectid, response } = action.payload;
      state.isLoading = false;
      state.versions[objectid] = response;
      state.hasMore[objectid] = response.length === VERSION_PAGE_SIZE;
    },
    versionsLoadMoreReceived: (state, action) => {
      const { objectid, response } = action.payload;
      state.isLoading = false;
      state.versions[objectid] = [...(state.versions[objectid] ?? []), ...response];
      state.hasMore[objectid] = response.length === VERSION_PAGE_SIZE;
    },
    versionsFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    associatedVersionsReceived: (state, action) => {
      const { objectid, response } = action.payload;
      state.isLoading = false;
      state.associatedVersions[objectid] = response;
      state.associatedHasMore[objectid] = response.length === VERSION_PAGE_SIZE;
    },
    associatedVersionsLoadMoreReceived: (state, action) => {
      const { objectid, response } = action.payload;
      state.isLoading = false;
      state.associatedVersions[objectid] = [...(state.associatedVersions[objectid] ?? []), ...response];
      state.associatedHasMore[objectid] = response.length === VERSION_PAGE_SIZE;
    },
    restoreRequested: (state) => {
      state.isRestoring = true;
      state.error = null;
    },
    restoreSucceeded: (state) => {
      state.isRestoring = false;
    },
    restoreFailed: (state, action) => {
      state.isRestoring = false;
      state.error = action.payload;
    },
    versionsReset: (state, action) => {
      const { objectid } = action.payload;
      delete state.versions[objectid];
      delete state.hasMore[objectid];
      delete state.associatedVersions[objectid];
      delete state.associatedHasMore[objectid];
    },
    versionDeleted: (state, action) => {
      const { objectid, versionid } = action.payload;
      if (state.versions[objectid]) {
        state.versions[objectid] = state.versions[objectid].filter((v) => v.versionid !== versionid);
      }
    },
    associatedVersionDeleted: (state, action) => {
      const { objectid, versionid } = action.payload;
      if (state.associatedVersions[objectid]) {
        state.associatedVersions[objectid] = state.associatedVersions[objectid].filter((v) => v.versionid !== versionid);
      }
    },
    versionDataReceived: (state, action) => {
      const { versionid, data } = action.payload;
      state.isLoading = false;
      state.versionData[versionid] = data;
    },
    associatedVersionDataReceived: (state, action) => {
      const { versionid, data } = action.payload;
      state.isLoading = false;
      state.associatedVersionData[versionid] = data;
    },
  },
});

export const {
  versionsRequested,
  versionsReceived,
  versionsLoadMoreReceived,
  versionsFailed,
  associatedVersionsReceived,
  associatedVersionsLoadMoreReceived,
  restoreRequested,
  restoreSucceeded,
  restoreFailed,
  versionsReset,
  versionDeleted,
  associatedVersionDeleted,
  versionDataReceived,
  associatedVersionDataReceived,
} = version.actions;

export default version.reducer;

export const getObjectVersions = (objectid: string) => apiCallBegan({
  url: `/object/${objectid}/versions?limit=${VERSION_PAGE_SIZE}&offset=0`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectVersion[]) => ({
    type: versionsReceived.type,
    payload: { objectid, response },
  }),
  onError: versionsFailed.type,
});

export const loadMoreObjectVersions = (objectid: string, offset: number) => apiCallBegan({
  url: `/object/${objectid}/versions?limit=${VERSION_PAGE_SIZE}&offset=${offset}`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectVersion[]) => ({
    type: versionsLoadMoreReceived.type,
    payload: { objectid, response },
  }),
  onError: versionsFailed.type,
});

export const getObjectAssociatedVersions = (objectid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/associated?limit=${VERSION_PAGE_SIZE}&offset=0`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectAssociatedVersion[]) => ({
    type: associatedVersionsReceived.type,
    payload: { objectid, response },
  }),
  onError: versionsFailed.type,
});

export const loadMoreObjectAssociatedVersions = (objectid: string, offset: number) => apiCallBegan({
  url: `/object/${objectid}/versions/associated?limit=${VERSION_PAGE_SIZE}&offset=${offset}`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectAssociatedVersion[]) => ({
    type: associatedVersionsLoadMoreReceived.type,
    payload: { objectid, response },
  }),
  onError: versionsFailed.type,
});

export const restoreObjectVersion = (objectid: string, versionid: string, nodeid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/${versionid}/restore`,
  method: 'POST',
  onStart: restoreRequested.type,
  onSuccess: () => ({ type: restoreSucceeded.type }),
  // @ts-ignore
  onAfterSuccess: () => (dispatch: any, getState: any) => {
    const unitTemplateQuantitiesGrouped = getUnitTemplateQuantitiesGroupedSelector(getState());
    dispatch(getObjectsOfNode(nodeid, unitTemplateQuantitiesGrouped));
    dispatch({ type: versionsReset.type, payload: { objectid } });
    dispatch(getObjectVersions(objectid));
    dispatch(getObjectAssociatedVersions(objectid));
  },
  onError: restoreFailed.type,
  toastOnSuccess: true,
  successMessage: 'Version restored successfully',
});

export const restoreObjectAssociatedVersion = (objectid: string, versionid: string, nodeid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/associated/${versionid}/restore`,
  method: 'POST',
  onStart: restoreRequested.type,
  onSuccess: () => ({ type: restoreSucceeded.type }),
  // @ts-ignore
  onAfterSuccess: () => (dispatch: any, getState: any) => {
    const unitTemplateQuantitiesGrouped = getUnitTemplateQuantitiesGroupedSelector(getState());
    dispatch(getObjectsOfNode(nodeid, unitTemplateQuantitiesGrouped));
    dispatch({ type: versionsReset.type, payload: { objectid } });
    dispatch(getObjectVersions(objectid));
    dispatch(getObjectAssociatedVersions(objectid));
  },
  onError: restoreFailed.type,
  toastOnSuccess: true,
  successMessage: 'Associated data version restored successfully',
});

export const deleteObjectVersion = (objectid: string, versionid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/${versionid}`,
  method: 'DELETE',
  // @ts-ignore
  onSuccess: () => ({
    type: versionDeleted.type,
    payload: { objectid, versionid },
  }),
  onError: versionsFailed.type,
});

export const deleteObjectAssociatedVersion = (objectid: string, versionid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/associated/${versionid}`,
  method: 'DELETE',
  // @ts-ignore
  onSuccess: () => ({
    type: associatedVersionDeleted.type,
    payload: { objectid, versionid },
  }),
  onError: versionsFailed.type,
});

export const getObjectVersion = (objectid: string, versionid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/${versionid}`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectVersion) => ({
    type: versionDataReceived.type,
    payload: { versionid, data: response.data },
  }),
  onError: versionsFailed.type,
});

export const getObjectAssociatedVersion = (objectid: string, versionid: string) => apiCallBegan({
  url: `/object/${objectid}/versions/associated/${versionid}`,
  method: 'GET',
  onStart: versionsRequested.type,
  // @ts-ignore
  onSuccess: (response: IObjectAssociatedVersion) => ({
    type: associatedVersionDataReceived.type,
    payload: { versionid, data: response.data },
  }),
  onError: versionsFailed.type,
});

